<template>
  <div class="bg-white py-16">
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div class="text-center mb-12">
        <h2 class="section-title">Suivez-nous</h2>
        <p class="section-subtitle">
          Découvrez nos dernières actualités et moments musicaux sur les réseaux sociaux
        </p>
      </div>
      
      <!-- Social media feeds -->
      <div class="py-8">
        <!-- Tab navigation -->
        <div class="flex justify-center mb-12">
          <div class="bg-gray-100 rounded-lg p-1">
            <button 
              @click="activeTab = 'facebook'"
              :class="[
                'px-6 py-3 rounded-md text-sm font-medium transition-colors',
                activeTab === 'facebook' 
                  ? 'bg-blue-600 text-white' 
                  : 'text-gray-700 hover:text-blue-600'
              ]"
            >
              Facebook
            </button>
            <button 
              @click="activeTab = 'instagram'"
              :class="[
                'px-6 py-3 rounded-md text-sm font-medium transition-colors',
                activeTab === 'instagram' 
                  ? 'bg-pink-600 text-white' 
                  : 'text-gray-700 hover:text-pink-600'
              ]"
            >
              Instagram
            </button>
            <button 
              @click="activeTab = 'spotify'"
              :class="[
                'px-6 py-3 rounded-md text-sm font-medium transition-colors',
                activeTab === 'spotify' 
                  ? 'bg-green-600 text-white' 
                  : 'text-gray-700 hover:text-green-600'
              ]"
            >
              Spotify
            </button>
          </div>
        </div>

        <!-- Facebook Tab -->
        <div v-if="activeTab === 'facebook'" class="space-y-8">
          <div class="text-center mb-8">
            <h2 class="text-3xl font-bold text-gray-900 mb-4">Facebook</h2>
            <p class="text-gray-600">
              Suivez nos dernières actualités et événements
            </p>
          </div>
          
          <!-- Facebook Page Plugin -->
          <div class="flex justify-center">
            <div class="w-full max-w-lg mx-auto">
              <div class="bg-white rounded-lg shadow-lg p-4">
                <div class="flex justify-center">
                  <iframe 
                    src="https://www.facebook.com/plugins/page.php?href=https%3A%2F%2Fwww.facebook.com%2Femulationstavelot&tabs=timeline%2Cevents&width=400&height=600&small_header=false&adapt_container_width=true&hide_cover=false&show_facepile=true&appId"
                    width="400" 
                    height="600" 
                    style="border:none;overflow:hidden" 
                    scrolling="no" 
                    frameborder="0" 
                    allowfullscreen="true" 
                    allow="autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share"
                    class="rounded-lg shadow-md"
                  ></iframe>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Instagram Tab -->
        <div v-if="activeTab === 'instagram'" class="space-y-8">
          <div class="text-center mb-8">
            <h2 class="text-3xl font-bold text-gray-900 mb-4">Instagram</h2>
            <p class="text-gray-600">
              Découvrez nos photos et moments musicaux
            </p>
          </div>
          
          <div class="max-w-4xl mx-auto">
            <div class="bg-white rounded-lg shadow-lg p-6">
              <div class="text-center mb-6">
                <div class="flex items-center justify-center mb-4">
                  <svg class="w-8 h-8 text-pink-600 mr-3" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12.017 0C5.396 0 .029 5.367.029 11.987c0 6.62 5.367 11.987 11.988 11.987s11.987-5.367 11.987-11.987C24.014 5.367 18.647.001 12.017.001zM8.449 16.988c-1.297 0-2.448-.49-3.323-1.297C4.198 14.895 3.708 13.744 3.708 12.447s.49-2.448 1.418-3.323c.875-.807 2.026-1.297 3.323-1.297s2.448.49 3.323 1.297c.928.875 1.418 2.026 1.418 3.323s-.49 2.448-1.418 3.244c-.875.807-2.026 1.297-3.323 1.297zm7.83-9.281H7.83c-.49 0-.928.438-.928.928v7.83c0 .49.438.928.928.928h8.449c.49 0 .928-.438.928-.928V8.635c0-.49-.438-.928-.928-.928z"/>
                  </svg>
                  <h3 class="text-2xl font-bold text-gray-900">@emulation_stavelot</h3>
                </div>
                <p class="mt-4 text-gray-600">
                  @emulation_stavelot
                </p>
              </div>
              
              <!-- Instagram Widget -->
              <div class="flex justify-center">
                <div class="instagram-embed-container">
                  <iframe 
                    src="https://www.instagram.com/emulation_stavelot/embed/" 
                    width="100%" 
                    height="480" 
                    frameborder="0" 
                    scrolling="no" 
                    allowtransparency="true"
                    class="rounded-lg shadow-md w-full"
                  ></iframe>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Spotify Tab -->
        <div v-if="activeTab === 'spotify'" class="space-y-8">
          <div class="text-center mb-8">
            <h2 class="text-3xl font-bold text-gray-900 mb-4">Spotify</h2>
            <p class="text-gray-600">
              Écoutez notre musique et nos enregistrements
            </p>
          </div>
          
          <div class="max-w-4xl mx-auto">
            <div class="bg-white rounded-lg shadow-lg p-6">
              <div class="text-center mb-6">
                <div class="flex items-center justify-center mb-4">
                  <svg class="w-8 h-8 text-green-600 mr-3" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.42 1.56-.299.421-1.02.599-1.559.3z"/>
                  </svg>
                  <h3 class="text-2xl font-bold text-gray-900">Royale Harmonie l'Émulation</h3>
                </div>
                <p class="mt-4 text-gray-600">
                  Notre profil Spotify officiel
                </p>
              </div>
              
              <!-- Spotify Embed -->
              <div class="flex justify-center">
                <iframe 
                  style="border-radius:12px" 
                  src="https://open.spotify.com/embed/artist/3goQVcNG0KIOQS6nEhh518?utm_source=generator&theme=0" 
                  width="100%" 
                  height="352" 
                  frameBorder="0" 
                  allowfullscreen="" 
                  allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture" 
                  loading="lazy"
                  class="rounded-lg shadow-md">
                </iframe>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'NewsSection',
  data() {
    return {
      activeTab: 'facebook'
    }
  },
  methods: {
    // Méthodes pour la gestion des onglets si nécessaire
  }
}
</script>

<style scoped>
.section-title {
  @apply text-3xl font-bold text-gray-900 mb-4;
}

.section-subtitle {
  @apply text-lg text-gray-600 max-w-3xl mx-auto;
}
</style>
